import styled, { keyframes } from "styled-components";
import { StyledText } from "./Text";
import FlexBox, { FlexGrow } from "./FlexBox";
import Icon from "./Icon";

export type EventProps = {
  title: string;
  description: string;
  startDate: string;
  endDate: string;
  left: string;
  top: string;
  height: string;
  backgroundColor: string;
  fontColor: string;
  skills?: any;
  credits?: any;
  competency_code?: string;
  
  fitScore?: number | null; // for score & and reason
  reason?: string | null;
  showPersonalizedFit?: boolean;
  pendingApproval?: boolean;
};
export type EventPropsFull = { onItemClick: () => void } & EventProps;

const MIN_TIMELINE_CARD_WIDTH_PX = 200;

function shouldShowFitAnnotation(
  fitScore?: number | null,
  reason?: string | null
) {
  const hasScore = typeof fitScore === "number" && Number.isFinite(fitScore);
  const hasReason =
    typeof reason === "string" && reason.trim().length > 0;
  return hasScore || hasReason;
}

function formatFitScoreDisplay(fitScore?: number | null): string {
  if (typeof fitScore !== "number" || !Number.isFinite(fitScore)) {
    return "";
  }
  return fitScore.toFixed(5);
}

const EventAnchorRow = styled.div<{ $left: string; $top: string }>`
  position: absolute;
  left: ${({ $left }) => $left};
  top: ${({ $top }) => $top};
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 12px;
  z-index: 5;
  pointer-events: none;
`;

const FitAnnotation = styled.div`
  pointer-events: none;
  font-family: Inter, sans-serif;
  font-size: 12px;
  line-height: 1.35;
  color: rgba(255, 255, 255, 0.92);
  max-width: min(440px, 42vw);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const pulseBorder = keyframes`
  0%, 100% { box-shadow: inset 0 0 0 2px rgba(217, 119, 6, 1), 0 0 0 0 rgba(217, 119, 6, 0.7), 0 0 0 3px rgba(217, 119, 6, 0.3); }
  50% { box-shadow: inset 0 0 0 2px rgba(217, 119, 6, 1), 0 0 0 6px rgba(217, 119, 6, 0), 0 0 0 10px rgba(217, 119, 6, 0); }
`;

type StyledEventBoxProps = EventProps & { $absolute: boolean };

const StyledEventBox = styled(FlexBox) <StyledEventBoxProps>`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  flex-wrap: nowrap;
  overflow: hidden;
  padding: 8px 12px;
  cursor: pointer;
  position: ${({ $absolute }) => ($absolute ? "absolute" : "relative")};
  height: auto;
  box-sizing: border-box;
  width: ${({ height }) => height};
  min-width: ${MIN_TIMELINE_CARD_WIDTH_PX}px;
  left: ${({ left, $absolute }) => ($absolute ? left : "auto")};
  top: ${({ top, $absolute }) => ($absolute ? top : "auto")};
  pointer-events: auto;
  background: ${({ backgroundColor }) => backgroundColor};
  border-radius: 8px;
  color: ${({ fontColor }) => fontColor};
  border: none;
  animation: ${({ pendingApproval }) => pendingApproval ? pulseBorder : "none"} 2s ease-in-out infinite;

  i {
    font-style: normal;
    margin-right: 5px;
  }
`;
const StyledEvent = ({
  onItemClick,
  fitScore,
  reason,
  showPersonalizedFit,
  ...props
}: EventPropsFull): JSX.Element => {
  const showFit =
    Boolean(showPersonalizedFit) &&
    shouldShowFitAnnotation(fitScore, reason);
  const scoreText = formatFitScoreDisplay(fitScore);

  const cardBody = (
    <>
      <FlexGrow
        style={{ flexGrow: 0, whiteSpace: "nowrap", marginRight: "8px" }}
      >
        <Icon size={2.625} fixedWidth={false} name="clock-eight"></Icon>
        <StyledText>
          {props.startDate}
          {" - "}
          {props.endDate}
        </StyledText>
      </FlexGrow>
      <FlexGrow style={{ flexGrow: 0, whiteSpace: "nowrap" }}>
        <StyledText
          fontWeight="700"
          style={{ fontSize: "16px", marginRight: "8px" }}
        >
          [{props.competency_code}] {props.title}
        </StyledText>
        <StyledText
          color="white"
          style={{ fontFamily: "Montserrat" }}
          fontWeight="600"
        >
          | Credits: {props.credits}
        </StyledText>
      </FlexGrow>
      {props.pendingApproval && (
        <div style={{
          position: "absolute",
          top: "4px",
          right: "4px",
          background: "linear-gradient(135deg, #b45309, #d97706)",
          color: "white",
          fontSize: "10px",
          fontWeight: "bold",
          textTransform: "uppercase",
          padding: "2px 8px",
          borderRadius: "10px",
          boxShadow: "0 2px 4px rgba(217,119,6,0.4)",
          pointerEvents: "none"
        }}>
          ⏳ Pending
        </div>
      )}
    </>
  );

  const box = (
    <StyledEventBox
      space={0}
      {...props}
      $absolute={!showFit}
      onClick={() => {
        console.log("Event clicked:", props.title);
        onItemClick();
      }}
    >
      {cardBody}
    </StyledEventBox>
  );

  if (!showFit) {
    return <>{box}</>;
  }

  const scorePart = scoreText ? `Score: ${scoreText}` : "";
  const reasonPart =
    typeof reason === "string" && reason.trim().length > 0
      ? `Reason: ${reason.trim()}`
      : "";
  const annotationText = [scorePart, reasonPart]
    .filter(Boolean)
    .join("  |  ");

  return (
    <EventAnchorRow $left={props.left} $top={props.top}>
      {box}
      {annotationText ? (
        <FitAnnotation title={annotationText}>{annotationText}</FitAnnotation>
      ) : null}
    </EventAnchorRow>
  );
};

export function PersonalizedFit({
  fitScore,
  reason,
}: {
  fitScore?: number | null;
  reason?: string | null;
}): JSX.Element | null {
  if (!shouldShowFitAnnotation(fitScore, reason)) {
    return null;
  }
  const scoreText = formatFitScoreDisplay(fitScore);
  const scorePart = scoreText ? `Score: ${scoreText}` : "";
  const reasonPart =
    typeof reason === "string" && reason.trim().length > 0
      ? `Reason: ${reason.trim()}`
      : "";
  const annotationText = [scorePart, reasonPart]
    .filter(Boolean)
    .join("  |  ");
  if (!annotationText) {
    return null;
  }
  return (
    <FitAnnotation title={annotationText}>{annotationText}</FitAnnotation>
  );
}

export default StyledEvent;
